import { ChangeEvent, useCallback } from 'react'

import { ENTITY_TYPES, updateLoadingState } from '@sales-center/common'
import {
  IEntityFormSetting,
  toggleActive,
  toggleRequired,
  updateOptions,
} from '@user/store/entityFormSettings'
import { useAppDispatch } from '@user/store/hooks'

import { addToPestPacOff, addToPestPacOn } from '../constants'
import { getFieldRequiredGroup } from '../config/fieldConfigs'
import { requiredGroups } from '../config/requiredGroups'

type Props = {
  allSettings: IEntityFormSetting[]
  settingItem: IEntityFormSetting
  entityType: ENTITY_TYPES
}

/**
 * Whether a field's "required" or "active" toggle may be turned off.
 *
 * Enforces the group rule in `config/requiredGroups.ts`: at least one member
 * of a required group must stay required. Turning one off is blocked only when
 * none of the field's other group members are currently required. The field's
 * group comes from its registry entry, so membership is declared in one place.
 *
 * Fields with no required group, and fields that are not currently required,
 * are never gated.
 *
 * @param allSettings The entity's field settings, used to read sibling state.
 * @param fieldId The field whose toggle is being changed.
 * @param entityType Which entity's field registry to resolve the group from.
 */
function canChangeRequiredOrActive(
  allSettings: IEntityFormSetting[],
  fieldId: IEntityFormSetting['fieldId'],
  entityType: ENTITY_TYPES,
): boolean {
  const group = getFieldRequiredGroup(entityType, fieldId)
  if (!group) return true

  const field = allSettings.find((x) => x.fieldId === fieldId)
  if (!field?.required) return true

  return requiredGroups[group]
    .filter((otherId) => otherId !== fieldId)
    .some(
      (otherId) => !!allSettings.find((x) => x.fieldId === otherId)?.required,
    )
}

/**
 * Builds the toggle and option handlers for one field settings card.
 *
 * Each handler dispatches the corresponding update thunk and surfaces failures
 * as a toast. Toggling "required" off is gated by the field's required group,
 * so a group never ends up with none of its members required.
 */
export function useFieldSettingsHandlers(props: Props) {
  const { allSettings, settingItem, entityType } = props
  const { id, fieldId } = settingItem
  const dispatch = useAppDispatch()

  const onChangeRequired = useCallback(async () => {
    if (!canChangeRequiredOrActive(allSettings, fieldId, entityType)) return

    const field = allSettings.find((x) => x.fieldId === fieldId)
    if (field) {
      dispatch(updateLoadingState('visible'))
      if (!field.active && !field.required)
        await dispatch(toggleActive({ id: id, toggleState: !field.active }))
      await dispatch(toggleRequired({ id: id, toggleState: !field.required }))
      dispatch(updateLoadingState('hidden'))
    }
  }, [dispatch, id, allSettings, fieldId, entityType])

  const onChangeEnabled = useCallback(async () => {
    if (!canChangeRequiredOrActive(allSettings, fieldId, entityType)) return

    const field = allSettings.find((x) => x.fieldId === fieldId)
    if (field) {
      dispatch(updateLoadingState('visible'))
      if (field.active && field.required)
        await dispatch(toggleRequired({ id: id, toggleState: !field.required }))
      await dispatch(toggleActive({ id: id, toggleState: !field?.active }))
      dispatch(updateLoadingState('hidden'))
    }
  }, [dispatch, id, allSettings, fieldId, entityType])

  const onChangeOption = useCallback(
    async (_event: ChangeEvent<HTMLInputElement>, value: string) => {
      if (!canChangeRequiredOrActive(allSettings, fieldId, entityType)) return

      const field = allSettings.find((x) => x.fieldId === fieldId)
      if (field) {
        dispatch(updateLoadingState('visible'))
        await dispatch(updateOptions({ id: id, options: value }))
        dispatch(updateLoadingState('hidden'))
      }
    },
    [dispatch, id, allSettings, fieldId, entityType],
  )

  const onChangeNameMode = useCallback(
    async (_event: ChangeEvent<HTMLInputElement>, value: string) => {
      dispatch(updateLoadingState('visible'))
      await dispatch(updateOptions({ id: id, options: value }))
      dispatch(updateLoadingState('hidden'))
    },
    [dispatch, id],
  )

  const onChangeLocationOption = useCallback(
    async (_event: ChangeEvent<HTMLInputElement>, value: string | number) => {
      const field = allSettings.find((x) => x.fieldId === fieldId)
      if (field) {
        dispatch(updateLoadingState('visible'))
        await dispatch(updateOptions({ id: id, options: value }))

        if (value == addToPestPacOn && !field.active) {
          await dispatch(toggleActive({ id: id, toggleState: !field?.active }))
        }

        dispatch(updateLoadingState('hidden'))
      }
    },
    [dispatch, id, allSettings, fieldId],
  )

  const onChangeLocationEnabled = useCallback(
    async (_event: ChangeEvent<HTMLInputElement>, value: boolean) => {
      const field = allSettings.find((x) => x.fieldId === fieldId)

      if (field) {
        dispatch(updateLoadingState('visible'))

        if (!value) {
          await dispatch(updateOptions({ id: id, options: addToPestPacOff }))
        }

        await dispatch(toggleActive({ id: id, toggleState: !field?.active }))
        dispatch(updateLoadingState('hidden'))
      }
    },
    [dispatch, id, allSettings, fieldId],
  )

  return {
    onChangeRequired,
    onChangeEnabled,
    onChangeOption,
    onChangeNameMode,
    onChangeLocationOption,
    onChangeLocationEnabled,
  }
}
